<template>
  <template v-if="isProductCard">
    <ul
      class="color-swatches"
    >
      <template v-for="(product, productIndex) of colorOptions">
        <li
          v-for="(_, colorIndex) of product.colorCodes"
          :key="`${productIndex}-${colorIndex}`"
          class="color-swatches__swatch-container"
        >
          <color-swatch
            v-if="!disabledCodes.includes(product.colorCodes[colorIndex])"
            :active-product-handle="productHandle"
            :color-code="product.colorCodes[colorIndex]"
            :color-label="product.colorLabels[colorIndex]"
            :color-value="product.values[colorIndex]"
            :featured-image-src="featuredImageSrc"
            :is-product-card="isProductCard"
            :namespace="namespace"
            :product-handle="product.handle"
            :variants="product.variants"
            @update-active-swatch="handleActiveSwatch"
          />
        </li>
      </template>

      <li v-if="renderPlusIcon">
        <a
          class="color-swatches__plus"
          :href="productUrl"
        >
          <span
            class="visually-hidden"
            v-text="$string('product.card.more_colors')"
          />

          <icon-plus />
        </a>
      </li>
    </ul>
  </template>

  <div
    v-else-if="isLineItem"
    class="color-swatches"
  >
    <color-swatch
      v-for="(_, index) of renderedColorOptions"
      :key="index"
      :is-active="isActive"
      :color-code="colorOptions.colorCodes[index]"
      :color-label="colorOptions.colorLabels[index]"
      :color-value="colorOptions.values[index]"
      :featured-image-src="featuredImageSrc"
      :input-name="inputName"
      :namespace="namespace"
      :product-handle="productHandle"
      :variants="variants"
      :set-initial-swatch="setInitialSwatch"
      :is-line-item="isLineItem"
      @update-active-swatch="handleActiveSwatch"
    />
  </div>

  <div
    v-else
    class="color-swatches"
  >
    <template v-for="(product, productIndex) of colorOptions">
      <template v-for="(_, colorIndex) of product.colorCodes">
        <color-swatch
          v-if="!disabledCodes.includes(product.colorCodes[colorIndex])"
          :key="`${productIndex}-${colorIndex}`"
          :is-active="isActive"
          :active-product-handle="productHandle"
          :color-code="product.colorCodes[colorIndex]"
          :color-label="product.colorLabels[colorIndex]"
          :color-value="product.values[colorIndex]"
          :featured-image-src="featuredImageSrc"
          :input-name="inputName"
          :namespace="namespace"
          :product-handle="product.handle"
          :variants="product.variants"
          :set-initial-swatch="setInitialSwatch"
          :is-line-item="isLineItem"
          @update-active-swatch="handleActiveSwatch"
        />
      </template>
    </template>
  </div>
</template>

<script>

/**
 * Vue: Color swatches (color-swatches)
 * -----------------------------------------------------------------------------
 * Snippet to handle color swatches for products.
 *
 * @param {Object} colorOptions - Object with all color data.
 * @param {Array} disabledCodes - Array of disabled color codes.
 * @param {String} featuredImageSrc - Featured image source URL.
 * @param {String} [inputName] - Input name (PDP).
 * @param {Boolean} [isProductCard] - If swatches are imported by product card.
 * @param {String} namespace - Color swatch namespace.
 * @param {String} productHandle - Product handle for current product.
 * @param {String} productUrl - Product URL.
 * @param {Boolean} renderPlusIcon - Whether to render plus icon.
 * @param {Array} variants - Product variants array.
 * @param {Array} isActive - If dropdown in cart is active.
 *
 * @emits updateActiveSwatch - Emit active color code.
 *
 */
import IconPlus from '~icons/general/plus-3.svg'
import ColorSwatch from './color-swatch'

export default {
  name: 'ColorSwatches',

  components: {
    ColorSwatch,
    IconPlus,
  },

  props: {
    colorOptions: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    disabledCodes: {
      type: Array,
      default: () => [],
    },
    featuredImageSrc: {
      type: String,
      default: '',
      required: true,
    },
    inputName: {
      type: String,
      default: '',
    },
    isProductCard: {
      type: Boolean,
      default: false,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    productHandle: {
      type: String,
      default: '',
      required: true,
    },
    productUrl: {
      type: String,
      default: '',
    },
    renderPlusIcon: {
      type: Boolean,
      default: false,
    },
    setInitialSwatch: {
      type: Boolean,
      default: true,
    },
    isLineItem: {
      type: Boolean,
      default: false,
    },
    variants: {
      type: [Boolean, Array],
      default: false,
      required: true,
    },
    isActive: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  emits: {
    'update-active-swatch': (payload) => typeof payload === 'object',
  },

  computed: {

    /**
     * Compute how many color swatches to display. Max of 8 on product cards.
     * - Only render color codes if they're not disabled via product tag.
     * @returns {Array}
     */
    renderedColorOptions() {
      if (this.isLineItem) {
        const filteredColorCodes = this.colorOptions.colorCodes.filter(colorCode => {
          return !this.disabledCodes.includes(colorCode)
        })

        return filteredColorCodes

      }

      const renderedColorOptions = []

      this.colorOptions.forEach(product => {
        if (this.disabledCodes.includes(product.colorCodes)) {
          return
        }
        renderedColorOptions.push(product.colorCodes)
      })
      return renderedColorOptions
    },
  },

  methods: {

    /**
    * Handle events when the active swatch is updated.
    * @param {Object} payload - Data from emitter.
    */
    handleActiveSwatch(payload) {
      if (this.isProductCard) {
        this.$emit('update-active-swatch', payload)
        return
      }

      if (payload?.handle && payload.handle !== this.productHandle) {
        cnvs.EventBus.emit('update-product', payload)
        return
      }

      this.$emit('update-active-swatch', payload)
    },
  },
}
</script>

<style lang="scss">
  @import './color-swatches';
</style>
